import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { confirmSignIn, signIn, signInWithRedirect } from 'aws-amplify/auth';
import { useAuth } from '../auth/AuthContext';
import { isCognitoOAuthConfigured, isCognitoPasswordSignInConfigured } from '../amplify-config';
import { formatAuthRedirectError, formatPasswordSignInError } from '../utils/authErrors';
import { PATIENTS_PATH } from '../constants/routes';
import { GoogleIcon } from './GoogleIcon';

const Login: React.FC = () => {
  const { user, isLoading, refreshUser } = useAuth();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user) {
    return <Navigate to={PATIENTS_PATH} replace />;
  }

  const handleGoogle = async () => {
    setError('');
    if (!isCognitoOAuthConfigured()) {
      setError('認証の設定が正しくありません。管理者にお問い合わせください。');
      return;
    }
    setSubmitting(true);
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (e) {
      setError(formatAuthRedirectError(e));
      setSubmitting(false);
    }
  };

  const handleEmailSignIn = async () => {
    setError('');
    if (!isCognitoPasswordSignInConfigured()) {
      setError('認証の設定が正しくありません。管理者にお問い合わせください。');
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }
    setSubmitting(true);
    try {
      const out = await signIn({
        username: trimmedEmail,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      });
      if (out.isSignedIn) {
        setPassword('');
        await refreshUser();
        return;
      }
      if (out.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setPassword('');
        setSubmitting(false);
        return;
      }
      setError('想定外のサインイン状態です。管理者にお問い合わせください。');
    } catch (e) {
      setError(formatPasswordSignInError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteNewPassword = async () => {
    setError('');
    if (newPassword.length < 8) {
      setError('パスワードは8文字以上にしてください。');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('新しいパスワードが一致しません。');
      return;
    }
    setSubmitting(true);
    try {
      const out = await confirmSignIn({ challengeResponse: newPassword });
      if (out.isSignedIn) {
        setNeedsNewPassword(false);
        setNewPassword('');
        setConfirmNewPassword('');
        await refreshUser();
        return;
      }
      setError('パスワードの設定を完了できませんでした。');
    } catch (e) {
      setError(formatPasswordSignInError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '85vh',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 380, width: '100%', textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/onod-abokado.png`}
            alt=""
            sx={{
              height: 96,
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        </Box>
        {needsNewPassword ? (
          <>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                初回ログインのため、新しいパスワードを設定してください（8文字以上・大文字・小文字・数字・記号を含む）。
              </Typography>
              <TextField
                label="新しいパスワード"
                type="password"
                fullWidth
                margin="dense"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <TextField
                label="新しいパスワード（確認）"
                type="password"
                fullWidth
                margin="dense"
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </Box>
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'left' }}>
                {error}
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ mt: 2 }}
              disabled={submitting}
              onClick={() => void handleCompleteNewPassword()}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'パスワードを設定して続行'}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', lineHeight: 1.7 }}>
              院内で使用するメールアドレスでログインしてください。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', lineHeight: 1.7 }}>
              Googleアカウントのユーザーは「Googleでログイン」からログインしてください。
            </Typography>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={submitting}
              onClick={() => void handleGoogle()}
              startIcon={submitting ? undefined : <GoogleIcon sx={{ fontSize: 22 }} />}
              sx={{
                py: 1.25,
                bgcolor: '#fff',
                color: 'rgba(0, 0, 0, 0.87)',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                boxShadow: 'none',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#fafafa',
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  bgcolor: '#fff',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.38)',
                },
              }}
            >
              Google でログイン
            </Button>
            <Divider sx={{ my: 3 }}>または</Divider>
            <Box
              component="form"
              sx={{ textAlign: 'left' }}
              onSubmit={(e) => {
                e.preventDefault();
                if (!email.trim()) return;
                void handleEmailSignIn();
              }}
            >
              <TextField
                label="メールアドレス"
                type="email"
                fullWidth
                margin="dense"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="パスワード"
                type="password"
                fullWidth
                margin="dense"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'left' }}>
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{ mt: 2 }}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'メールでログイン'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Login;
